/**
 * d-collection-sort-availability.js
 *
 * Ordena os produtos da coleção colocando disponíveis antes dos esgotados,
 * buscando todas as páginas da coleção e construindo uma paginação virtual
 * no cliente (sem recarregar a página).
 *
 * ── Como incluir ────────────────────────────────────────────────────────────
 * Adicione a tag abaixo no template de coleção (ex: sections/main-collection-product-grid.liquid):
 *
 *   <script src="{{ 'd-collection-sort-availability.js' | asset_url }}" defer></script>
 *
 * ── Pré-requisitos no HTML ───────────────────────────────────────────────────
 * O script depende de três elementos no DOM:
 *
 *   1. Grid de produtos com id="product-grid" e os data attributes:
 *        data-id="{{ section.id }}"
 *        data-per-page="{{ section.settings.products_per_page }}"
 *        data-total-pages="{{ paginate.pages }}"
 *
 *   2. Container externo com id="ProductGridContainer" — observado via
 *      MutationObserver para detectar re-renders do Dawn após filtros/sort AJAX.
 *
 *   3. Wrapper de paginação com classe "pagination-wrapper" — onde a paginação
 *      virtual será injetada.
 *
 * ── Marcação de disponibilidade nos cards ────────────────────────────────────
 * Cada <li> do grid deve ter o data attribute abaixo para que o script saiba
 * quais produtos estão esgotados:
 *
 *   data-product-available="{{ product.available }}"
 *   (Shopify renderiza como "true" ou "false")
 *
 * ── Contador de produtos (opcional) ─────────────────────────────────────────
 * Para exibir "Mostrando X–Y de Z produtos", adicione o atributo em qualquer
 * elemento da página:
 *
 *   <span data-collection-count></span>
 *
 * ── Constantes ajustáveis ────────────────────────────────────────────────────
 * Se o tema mudar os IDs/seletores padrão do Dawn, edite as constantes no topo
 * do script (GRID_ID, GRID_CONTAINER_ID, PAGINATION_SEL, MAX_PAGES).
 *
 * ── Comportamento ────────────────────────────────────────────────────────────
 * - Coleção com 1 página: reordena os cards diretamente no DOM (sem fetch).
 * - Coleção com múltiplas páginas: faz fetch paralelo de todas as páginas,
 *   une os cards, ordena e constrói paginação virtual client-side.
 * - Compatível com filtros e ordenação AJAX do Dawn (detecta re-renders via
 *   MutationObserver e reinicia o sort automaticamente).
 * - Compatível com navegação via browser back/forward (popstate).
 * - Em caso de erro no fetch, cai no reorder de DOM como fallback.
 */

(function () {
  'use strict';

  // ─── Selectors (adapt these if the theme changes) ──────────────────────────
  var GRID_ID           = 'product-grid';
  var GRID_CONTAINER_ID = 'ProductGridContainer';
  var PAGINATION_SEL    = '.pagination-wrapper';
  var MAX_PAGES         = 20;

  // ─── State ─────────────────────────────────────────────────────────────────
  var state = {
    products     : [],   // { type: 'product', html, soldOut }[]
    items        : [],   // ({ type:'product' } | { type:'banner' })[]
    banners      : [],   // { type: 'banner', html, position, span }[]
    perPage      : 8,
    visibleProducts: 0,
    sectionId    : '',
    totalPages   : 1,
    loadMoreLabel: 'Carregar mais',
    columnsMobile: 2,
    initialized  : false,
    fetching     : false,
    generation   : 0,
  };

  // ─── Read metadata from #product-grid data attributes ─────────────────────
  function readMeta() {
    var el = document.getElementById(GRID_ID);
    if (!el) return null;
    return {
      sectionId      : el.dataset.id,
      perPage        : parseInt(el.dataset.perPage, 10) || 24,
      totalPages     : parseInt(el.dataset.totalPages, 10) || 1,
      columnsMobile  : Math.max(1, parseInt(el.dataset.columnsMobile, 10) || 2),
      loadMoreLabel  : el.dataset.loadMoreLabel || 'Carregar mais',
      bannerTemplatesId: el.dataset.bannerTemplatesId || '',
    };
  }

  // ─── URL builder — preserves existing filter/sort params ──────────────────
  function buildUrl(page, sectionId) {
    var params = new URLSearchParams(window.location.search);
    params.set('page', page);
    params.set('section_id', sectionId);
    return window.location.pathname + '?' + params.toString();
  }

  // ─── Fetch all server pages in parallel ───────────────────────────────────
  function fetchAllPages(sectionId, totalPages) {
    var count = Math.min(totalPages, MAX_PAGES);
    var fetches = [];
    for (var i = 1; i <= count; i++) {
      fetches.push(
        fetch(buildUrl(i, sectionId)).then(function (r) { return r.text(); })
      );
    }
    return Promise.all(fetches);
  }

  // ─── Extract <li> cards from fetched HTML ─────────────────────────────────
  function extractCards(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    return Array.from(doc.querySelectorAll('#' + GRID_ID + ' > li')).map(function (li) {
      return {
        type   : 'product',
        html   : li.outerHTML,
        soldOut: li.dataset.productAvailable === 'false',
      };
    });
  }

  // ─── Sort: available first, sold-out last ─────────────────────────────────
  function sortCards(cards) {
    var available = cards.filter(function (c) { return !c.soldOut; });
    var soldOut   = cards.filter(function (c) { return c.soldOut; });
    return available.concat(soldOut);
  }

  // ─── Read banner templates rendered by Liquid blocks ───────────────────────
  function readBannerTemplates(templatesId) {
    if (!templatesId) return [];
    var root = document.getElementById(templatesId);
    if (!root) return [];

    return Array.from(root.querySelectorAll('template[data-banner-template]')).map(function (tpl) {
      return {
        type    : 'banner',
        html    : tpl.innerHTML.trim(),
        position: Math.max(1, parseInt(tpl.dataset.bannerPosition, 10) || 1),
        span    : Math.max(1, parseInt(tpl.dataset.bannerSpan, 10) || 1),
      };
    }).filter(function (banner) {
      return banner.html.length > 0;
    });
  }

  // ─── Update count text elements ───────────────────────────────────────────
  function updateCount(pageNum) {
    var total = state.products.length;
    if (total === 0) {
      document.querySelectorAll('[data-collection-count]').forEach(function (el) {
        el.textContent = 'Mostrando 0 de 0 produtos';
      });
      return;
    }

    var start = (pageNum - 1) * state.perPage + 1;
    var end   = Math.min(state.visibleProducts, total);
    var text  = 'Mostrando ' + start + '\u2013' + end + ' de ' + total + ' produtos';
    document.querySelectorAll('[data-collection-count]').forEach(function (el) {
      el.textContent = text;
    });
  }

  // ─── Build mixed array of products + banners by position ──────────────────
  function buildItems(products, banners) {
    if (!banners.length) return products.slice();

    var maxBannerPosition = products.length + 1;

    var orderedBanners = banners
      .filter(function (banner) {
        return banner.position <= maxBannerPosition;
      })
      .sort(function (a, b) {
      if (a.position === b.position) return a.span - b.span;
      return a.position - b.position;
    });

    var items = [];
    var bannerCursor = 0;
    var productIndex = 1;
    var rowFillMobile = 0;

    while (productIndex <= products.length) {
      while (bannerCursor < orderedBanners.length && orderedBanners[bannerCursor].position === productIndex) {
        if (state.columnsMobile > 1 && rowFillMobile !== 0) {
          rowFillMobile = 0;
        }
        items.push(orderedBanners[bannerCursor]);
        bannerCursor += 1;
      }

      items.push(products[productIndex - 1]);
      rowFillMobile = (rowFillMobile + 1) % state.columnsMobile;
      productIndex += 1;
    }

    while (bannerCursor < orderedBanners.length) {
      items.push(orderedBanners[bannerCursor]);
      bannerCursor += 1;
    }

    return items;
  }

  function isMobileViewport() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  function getItemsForViewport() {
    if (isMobileViewport()) return normalizeMobileBannerFlow(state.items);
    return state.items;
  }

  function normalizeMobileBannerFlow(items) {
    if (state.columnsMobile <= 1) return items;

    var normalized = [];
    var delayedBanners = [];
    var rowFillMobile = 0;

    for (var i = 0; i < items.length; i++) {
      var item = items[i];

      if (item.type === 'product') {
        normalized.push(item);
        rowFillMobile = (rowFillMobile + 1) % state.columnsMobile;

        if (rowFillMobile === 0 && delayedBanners.length) {
          normalized = normalized.concat(delayedBanners);
          delayedBanners = [];
          rowFillMobile = 0;
        }
        continue;
      }

      if (rowFillMobile === 0) {
        normalized.push(item);
      } else {
        delayedBanners.push(item);
      }
    }

    if (delayedBanners.length) {
      normalized = normalized.concat(delayedBanners);
    }

    return normalized;
  }

  function getVisibleItems(productCountTarget, sourceItems) {
    var itemsSource = sourceItems || state.items;
    var visible = [];
    var productsSeen = 0;
    var showingAllProducts = productCountTarget >= state.products.length;

    for (var i = 0; i < itemsSource.length; i++) {
      var item = itemsSource[i];

      if (item.type === 'product') {
        if (productsSeen >= productCountTarget) break;
        productsSeen += 1;
        visible.push(item);
      } else {
        if (!showingAllProducts && productsSeen >= productCountTarget) break;
        visible.push(item);
      }
    }

    return visible;
  }

  // ─── Render visible items into the grid ───────────────────────────────────
  function renderVisibleItems() {
    var grid = document.getElementById(GRID_ID);
    if (!grid || state.items.length === 0) return;

    var clampedVisibleProducts = state.visibleProducts;
    if (clampedVisibleProducts <= 0 && state.products.length > 0) {
      clampedVisibleProducts = Math.min(state.perPage, state.products.length);
      state.visibleProducts = clampedVisibleProducts;
    }

    var viewportItems = getItemsForViewport();
    var visible = getVisibleItems(clampedVisibleProducts, viewportItems);

    if (visible.length === 0) {
      grid.innerHTML = '';
      return;
    }

    grid.innerHTML = visible.map(function (item) { return item.html; }).join('');

    // Reset animation order on inserted items
    Array.from(grid.children).forEach(function (li, i) {
      li.style.setProperty('--animation-order', i + 1);
      li.style.opacity = '1';
      li.removeAttribute('reveal');
    });
  }

  function ensureLoadMoreWrapper() {
    var wrapper = document.querySelector(PAGINATION_SEL);
    if (wrapper) return wrapper;

    var grid = document.getElementById(GRID_ID);
    if (!grid || !grid.parentNode) return null;

    wrapper = document.createElement('div');
    wrapper.className = 'pagination-wrapper d-grid-load-more-wrapper';
    grid.parentNode.insertBefore(wrapper, grid.nextSibling);
    return wrapper;
  }

  // ─── Build load-more CTA with remaining count ─────────────────────────────
  function renderLoadMore() {
    var wrapper = ensureLoadMoreWrapper();
    if (!wrapper) return;

    var remaining = Math.max(state.products.length - state.visibleProducts, 0);
    if (remaining <= 0) {
      wrapper.innerHTML = '';
      return;
    }

    wrapper.classList.add('d-grid-load-more-wrapper');
    wrapper.innerHTML = '<button type="button" class="d-grid-load-more" data-vs-load-more>'
      + state.loadMoreLabel.toUpperCase() + ' (' + remaining + ')</button>';
  }

  // ─── Load next virtual chunk ───────────────────────────────────────────────
  function loadMoreProducts() {
    state.visibleProducts = Math.min(state.visibleProducts + state.perPage, state.products.length);

    renderVisibleItems();
    renderLoadMore();
    updateCount(1);
  }

  // ─── Attach click handler to load-more button ──────────────────────────────
  function attachLoadMoreListener() {
    var wrapper = ensureLoadMoreWrapper();
    if (!wrapper) return;
    wrapper.removeEventListener('click', onLoadMoreClick);
    wrapper.addEventListener('click', onLoadMoreClick);
  }

  function onLoadMoreClick(e) {
    var button = e.target.closest('button[data-vs-load-more]');
    if (!button) return;
    e.preventDefault();
    loadMoreProducts();
  }

  // ─── In-page DOM reorder (fallback for single-page collections) ───────────
  function reorderDOM() {
    var grid = document.getElementById(GRID_ID);
    if (!grid || state.items.length === 0) return;

    state.visibleProducts = Math.min(state.perPage, state.products.length);
    renderVisibleItems();
    renderLoadMore();
    updateCount(1);

    attachLoadMoreListener();
  }

  function collectCurrentPageProducts() {
    var grid = document.getElementById(GRID_ID);
    if (!grid) return [];

    var items    = Array.from(grid.children);
    return items.map(function (li) {
      return {
        type   : 'product',
        html   : li.outerHTML,
        soldOut: li.dataset.productAvailable === 'false',
      };
    });
  }

  // ─── Virtual sort orchestrator ────────────────────────────────────────────
  function initVirtualSort() {
    var meta = readMeta();
    if (!meta) return;

    state.sectionId = meta.sectionId;
    state.perPage   = meta.perPage;
    state.totalPages = meta.totalPages;
    state.columnsMobile = meta.columnsMobile;
    state.loadMoreLabel = meta.loadMoreLabel;
    state.banners = readBannerTemplates(meta.bannerTemplatesId);

    // Single-page collection: no fetch, but still sort + banner insertion + load more
    if (meta.totalPages <= 1) {
      state.products = sortCards(collectCurrentPageProducts());
      state.items = buildItems(state.products, state.banners);
      reorderDOM();
      return;
    }

    var gen = ++state.generation;
    state.fetching    = false;
    state.initialized = false;
    state.fetching    = true;

    // Hide grid to prevent flash of unsorted content
    var grid = document.getElementById(GRID_ID);
    if (grid) grid.style.visibility = 'hidden';

    fetchAllPages(state.sectionId, state.totalPages)
      .then(function (pages) {
        if (gen !== state.generation) return; // Stale — a newer sort was triggered

        var all = [];
        pages.forEach(function (html) {
          all = all.concat(extractCards(html));
        });

        state.products    = sortCards(all);
        state.items       = buildItems(state.products, state.banners);
        state.visibleProducts = Math.min(state.perPage, state.products.length);
        state.fetching    = false;
        state.initialized = true;

        renderVisibleItems();
        renderLoadMore();
        updateCount(1);
        attachLoadMoreListener();

        if (grid) grid.style.visibility = '';
      })
      .catch(function (err) {
        console.warn('[sort-availability] Fetch failed, falling back to in-page sort.', err);
        if (gen !== state.generation) return;
        state.fetching = false;
        if (grid) grid.style.visibility = '';
        state.products = sortCards(collectCurrentPageProducts());
        state.items = buildItems(state.products, state.banners);
        reorderDOM();
      });
  }

  // ─── MutationObserver — detect Dawn's facets/sort AJAX updates ────────────
  var _debounceTimer = null;

  function watchForUpdates() {
    var container = document.getElementById(GRID_CONTAINER_ID);
    if (!container) return;

    var observer = new MutationObserver(function (mutations) {
      // Only react when #product-grid itself is (re)inserted into the DOM
      var gridInserted = mutations.some(function (m) {
        return Array.from(m.addedNodes).some(function (n) {
          if (n.nodeType !== 1) return false;
          return n.id === GRID_ID
            || (typeof n.querySelector === 'function' && !!n.querySelector('#' + GRID_ID));
        });
      });

      if (!gridInserted || state.fetching) return;

      // Debounce: wait for URL (history.pushState) to update after DOM change
      clearTimeout(_debounceTimer);
      _debounceTimer = setTimeout(function () {
        state.products    = [];
        state.items       = [];
        state.banners     = [];
        state.visibleProducts = 0;
        state.initialized = false;
        initVirtualSort();
      }, 150);
    });

    observer.observe(container, { childList: true, subtree: true });
  }

  // ─── Boot ─────────────────────────────────────────────────────────────────
  function boot() {
    if (!document.getElementById(GRID_ID)) return;
    initVirtualSort();
    watchForUpdates();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();
